<template>
    <div
        ref="feed"
        :class="$style.feed"
        data-interface-section="mobile_feed"
    >
        <MobileFeedPage
            v-for="({ page, offset }, index) in batches"
            :key="index"
            :games="page.games"
            :positions="page.positions"
            :offset="offset"
            :slateId="page.slateId"
            :total="total"
        />
        <template v-if="isPending">
            <GameCard
                v-for="index in MOBILE_FEED_PAGE_SIZE"
                :key="`placeholder-${index}`"
                type="rectangle"
                responsive
            />
        </template>
        <Loader
            v-if="isFetchingNextPage"
            :class="$style.loader_override"
        />
    </div>
</template>
<script lang="ts" setup>
import { useThrottleFn } from '@vueuse/core'
import { computed, onMounted, onServerPrefetch, onUnmounted, useTemplateRef, watch } from 'vue'

import GameCard from '@/components/GameCard/GameCard.vue'
import Loader from '@/components/Loader.vue'

import { MOBILE_FEED_PAGE_SIZE, useMobileFeed } from '../../use-mobile-feed'
import MobileFeedPage from './MobileFeedPage.vue'

const feed = useTemplateRef('feed')
const { pages, isPending, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage, suspense } =
    useMobileFeed()

const batches = computed(() => {
    let offset = 0
    return pages.value.map((page) => {
        const batch = { page, offset }
        offset += page.games.length
        return batch
    })
})
const total = computed(() => pages.value[0]?.totalCount)

onServerPrefetch(suspense)

function fetchAhead() {
    const lastBatch = batches.value.at(-1)
    if (!feed.value || !lastBatch || !hasNextPage.value || isFetchNextPageError.value) {
        return
    }
    const firstCardOfLastBatch = feed.value.children[lastBatch.offset]
    if (!firstCardOfLastBatch || firstCardOfLastBatch.getBoundingClientRect().top < window.innerHeight) {
        fetchNextPage({ cancelRefetch: false })
    }
}

const onScroll = useThrottleFn(fetchAhead, 150, true)

function fetchAheadIfNothingToScroll() {
    if (document.documentElement.scrollHeight <= window.innerHeight) {
        fetchAhead()
    }
}

watch(
    () => pages.value.length,
    (length, loaded) => {
        if (!length) {
            window.scrollTo(0, 0)
        } else if (loaded) {
            fetchAhead()
        } else {
            fetchAheadIfNothingToScroll()
        }
    },
    { flush: 'post' },
)

onMounted(() => {
    window.addEventListener('scroll', onScroll, { passive: true })
    fetchAheadIfNothingToScroll()
})
onUnmounted(() => {
    window.removeEventListener('scroll', onScroll)
})
</script>
<style module>
.feed {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px 8px;
    --ctr-card-size: regular;
}

.loader_override {
    grid-column: 1 / -1;
    justify-self: center;
    margin: 8px 0;
    color: var(--white-60);
}

@media (width < 500px) {
    .feed > :nth-child(15n + 1):not(svg) {
        grid-column: span 2;
        --ctr-card-size: large;
    }
}

@media (width >= 500px) {
    .feed {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (width >= 640px) {
    .feed {
        grid-template-columns: repeat(4, 1fr);
    }
}

@media (640px <= width < 960px) {
    .feed > :nth-child(30n + 1):not(svg),
    .feed > :nth-child(30n + 2):not(svg) {
        grid-column: span 2;
        --ctr-card-size: large;
    }
}

@media (width >= 960px) {
    .feed {
        grid-template-columns: repeat(6, 1fr);
    }
}

@media (960px <= width < 1300px) {
    .feed > :nth-child(45n + 1):not(svg),
    .feed > :nth-child(45n + 2):not(svg),
    .feed > :nth-child(45n + 3):not(svg) {
        grid-column: span 2;
        --ctr-card-size: large;
    }
}

@media (width >= 1300px) {
    .feed {
        grid-template-columns: repeat(8, 1fr);
    }

    .feed > :nth-child(60n + 1):not(svg),
    .feed > :nth-child(60n + 2):not(svg),
    .feed > :nth-child(60n + 3):not(svg),
    .feed > :nth-child(60n + 4):not(svg) {
        grid-column: span 2;
        --ctr-card-size: large;
    }
}
</style>
